import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, StatusPill } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { StatusBadge } from '../../_shared/components/StatusBadge';
import { MethodPill } from '../../_shared/components/MethodPill';
import { GatewayConfirmDialog } from '../../_shared/components/GatewayConfirmDialog';
import { ComingSoonState } from '../../_shared/components/ComingSoonState';
import { ApiFormDialog } from './ApiFormDialog';
import { useApisQuery, useDeleteApi, useDeprecateApi, useApisSummary, useDiscoverApis } from '../hooks/useApis';
import { useHasRole } from '../../../_shared/hooks/useCurrentUser';
import type { ApiRow, ApiFilters } from '../types/api';
import type { StatusKind } from '../../_shared/components/StatusBadge';

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: '48px',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-md)',
          }}
        />
      ))}
    </div>
  );
}

const statusToKind: Record<string, StatusKind> = {
  active: 'active',
  deprecated: 'deprecated',
  disabled: 'disabled',
  // stale is rendered separately via StatusPill (warning tone), not via StatusBadge
};

export function ApisListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ApiFilters>({});
  const [page, setPage] = useState(0);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [selectedApi, setSelectedApi] = useState<ApiRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiRow | null>(null);
  const [deprecateTarget, setDeprecateTarget] = useState<ApiRow | null>(null);
  // #444 (UAT 2026-05-13): 'API 정보 관리란?' 도움말 패널 접힘 상태.
  const [helpOpen, setHelpOpen] = useState(true);
  // #473 (UAT 2026-06-02): API 자동수집 확인 다이얼로그 열림 여부.
  const [discoverDialogOpen, setDiscoverDialogOpen] = useState(false);

  // #417: EXEC sandbox detection — ROLE_EXEC + !ROLE_ADMIN
  const isExec = useHasRole('ROLE_EXEC');
  const isAdmin = useHasRole('ROLE_ADMIN');
  const isExecSandbox = isExec && !isAdmin;

  // #450 (UAT 2026-05-14): 삭제/지원종료 토스트 (성공/실패 메시지 노출).
  const [toast, setToast] = useState<{ message: string; kind: 'success' | 'error' } | null>(null);

  const { data, isLoading, isError } = useApisQuery(filters, page);
  const summary = useApisSummary();
  const deleteApi = useDeleteApi(
    () => {
      const deletedName = deleteTarget?.name ?? '';
      setDeleteTarget(null);
      setToast({ message: t('gateway.apis.toast.deletedVerify', { name: deletedName }), kind: 'success' });
      setTimeout(() => setToast(null), 2500);
    },
    (err) => {
      const msg = (err as { message?: string })?.message ?? t('gateway.shared.error.message');
      setToast({ message: msg, kind: 'error' });
      setTimeout(() => setToast(null), 3500);
    },
  );
  const deprecateApi = useDeprecateApi(
    () => {
      const deprecatedName = deprecateTarget?.name ?? '';
      setDeprecateTarget(null);
      setToast({ message: t('gateway.apis.toast.deprecated', { name: deprecatedName }), kind: 'success' });
      setTimeout(() => setToast(null), 2500);
    },
    (err) => {
      const msg = (err as { message?: string })?.message ?? t('gateway.shared.error.message');
      setToast({ message: msg, kind: 'error' });
      setTimeout(() => setToast(null), 3500);
    },
  );

  // #473 (UAT 2026-06-02): API 자동수집 mutation.
  const discoverApis = useDiscoverApis(
    (summary) => {
      setDiscoverDialogOpen(false);
      const msg = t('gateway.apis.discover.toast.success', {
        inserted: summary.inserted,
        updated: summary.updated,
        staled: summary.staled,
        total: summary.total,
      });
      setToast({ message: msg, kind: 'success' });
      setTimeout(() => setToast(null), 4000);
    },
    (err) => {
      setDiscoverDialogOpen(false);
      const status = (err as { status?: number })?.status;
      const httpStatus = status ?? ((err as { response?: { status?: number } })?.response?.status);
      let msg: string;
      if (httpStatus === 423) {
        msg = t('gateway.apis.discover.toast.sandbox');
      } else if (httpStatus === 403) {
        msg = t('gateway.shared.error.forbidden');
      } else {
        msg = (err as { message?: string })?.message ?? t('gateway.apis.discover.toast.error');
      }
      setToast({ message: msg, kind: 'error' });
      setTimeout(() => setToast(null), 4000);
    },
  );

  const columns: DataTableColumn<ApiRow>[] = [
    {
      key: 'name',
      header: t('gateway.apis.columns.name'),
      width: '160px',
      render: (row) => (
        <span
          style={{
            textDecoration: row.status === 'deprecated' ? 'line-through' : undefined,
            color: row.status === 'deprecated' ? 'var(--color-text-muted)' : undefined,
          }}
        >
          {row.name}
        </span>
      ),
    },
    {
      key: 'method',
      header: t('gateway.apis.columns.method'),
      width: '100px',
      render: (row) => <MethodPill method={row.method} size="sm" />,
    },
    {
      key: 'path',
      header: t('gateway.apis.columns.path'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: row.status === 'deprecated' ? 'var(--color-text-muted)' : undefined,
            textDecoration: row.status === 'deprecated' ? 'line-through' : undefined,
          }}
        >
          {row.path}
        </span>
      ),
    },
    {
      key: 'version',
      header: t('gateway.apis.columns.version'),
      width: '80px',
    },
    {
      key: 'authPolicy',
      header: t('gateway.apis.columns.auth'),
      width: '100px',
    },
    {
      key: 'status',
      header: t('gateway.apis.columns.status'),
      width: '140px',
      render: (row) => {
        // #473: stale 상태는 StatusPill(warning)으로 별도 렌더
        if (row.status === 'stale') {
          return (
            <span title={t('gateway.apis.badge.staleTooltip')}>
              <StatusPill tone="warning">
                &#9888; {t('gateway.apis.badge.stale')}
              </StatusPill>
            </span>
          );
        }
        return <StatusBadge status={statusToKind[row.status] ?? 'unknown'} size="sm" />;
      },
    },
    {
      // #473: 출처 컬럼 — 자동수집(autoDiscovered=true) vs 수동 등록
      key: 'autoDiscovered' as keyof ApiRow,
      header: t('gateway.apis.columns.source'),
      width: '80px',
      render: (row) => (
        <StatusPill tone={row.autoDiscovered ? 'info' : 'neutral'}>
          {row.autoDiscovered ? t('gateway.apis.badge.autoDiscovered') : t('gateway.apis.badge.manual')}
        </StatusPill>
      ),
    },
    {
      key: 'actions',
      header: t('gateway.apis.columns.actions'),
      width: '250px',
      render: (row) => {
        // #443 (UAT 2026-05-13): EXEC 가드를 systemManaged(=autoDiscovered) 기반으로 좁힘.
        // - 사용자 API(autoDiscovered=false): ADMIN/EXEC 모두 수정·삭제 허용
        // - 시드 API(autoDiscovered=true): ADMIN 도 본래 차단(기존 정책), EXEC 도 차단
        const isAuto = row.autoDiscovered === true;
        const autoTitle = t('gateway.apis.autoDiscovered.tooltip');
        const systemManagedTitle = t('gateway.apis.systemManaged.tooltip');
        // 시드 API 는 모두에게 차단(autoTitle). 사용자 API + EXEC 조합은 BE 가드 없음 → FE 도 활성.
        const isEditBlocked = isAuto;
        const isDeleteBlocked = isAuto;
        // deprecate: 시드 API 는 EXEC 차단(systemManagedTitle), 사용자 API 는 EXEC 도 허용
        const isDeprecateBlocked = isAuto && isExecSandbox;
        const editTitle = isAuto ? autoTitle : undefined;
        const deleteTitle = isAuto ? autoTitle : undefined;
        const deprecateTitle = isDeprecateBlocked ? systemManagedTitle : undefined;
        return (
          <div style={{ display: 'flex', gap: 'var(--spacing-1)', alignItems: 'center' }}>
            {isAuto && (
              <span
                title={autoTitle}
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  background: 'var(--color-surface-muted)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-1)',
                }}
              >
                {t('gateway.apis.autoDiscovered.badge')}
              </span>
            )}
            <button
              type="button"
              aria-label={`${row.name} ${t('gateway.shared.actions.edit')}`}
              disabled={isEditBlocked}
              aria-disabled={isEditBlocked}
              title={editTitle}
              onClick={(e) => { e.stopPropagation(); if (!isEditBlocked) { setSelectedApi(row); setDialogMode('edit'); } }}
              style={{ ...actionBtnStyle, opacity: isEditBlocked ? 0.4 : 1, cursor: isEditBlocked ? 'not-allowed' : 'pointer' }}
            >
              {t('gateway.shared.actions.edit')}
            </button>
            {row.status !== 'deprecated' && (
              <button
                type="button"
                aria-label={`${row.name} ${t('gateway.apis.actions.deprecate')}`}
                disabled={isDeprecateBlocked}
                aria-disabled={isDeprecateBlocked}
                title={deprecateTitle}
                onClick={(e) => { e.stopPropagation(); if (!isDeprecateBlocked) setDeprecateTarget(row); }}
                style={{ ...actionBtnStyle, color: isDeprecateBlocked ? undefined : 'var(--color-warning)', borderColor: isDeprecateBlocked ? undefined : 'var(--color-warning)', opacity: isDeprecateBlocked ? 0.4 : 1, cursor: isDeprecateBlocked ? 'not-allowed' : 'pointer' }}
              >
                {t('gateway.apis.actions.deprecate')}
              </button>
            )}
            <button
              type="button"
              aria-label={`${row.name} ${t('gateway.shared.actions.delete')}`}
              disabled={isDeleteBlocked}
              aria-disabled={isDeleteBlocked}
              title={deleteTitle}
              onClick={(e) => { e.stopPropagation(); if (!isDeleteBlocked) setDeleteTarget(row); }}
              style={{ ...actionBtnStyle, color: isDeleteBlocked ? undefined : 'var(--color-danger)', borderColor: isDeleteBlocked ? undefined : 'var(--color-danger)', opacity: isDeleteBlocked ? 0.4 : 1, cursor: isDeleteBlocked ? 'not-allowed' : 'pointer' }}
            >
              {t('gateway.shared.actions.delete')}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('gateway.apis.title')}
        breadcrumb={t('gateway.apis.breadcrumb')}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            {/* #473: API 자동수집 버튼 — ROLE_EXEC(sandbox) 는 disabled + tooltip */}
            <button
              type="button"
              disabled={isExecSandbox || discoverApis.isPending}
              aria-disabled={isExecSandbox || discoverApis.isPending}
              aria-busy={discoverApis.isPending}
              title={isExecSandbox ? t('gateway.apis.discover.toast.sandbox') : undefined}
              onClick={() => { if (!isExecSandbox && !discoverApis.isPending) setDiscoverDialogOpen(true); }}
              style={{
                ...secondaryBtnStyle,
                opacity: (isExecSandbox || discoverApis.isPending) ? 0.5 : 1,
                cursor: (isExecSandbox || discoverApis.isPending) ? 'not-allowed' : 'pointer',
              }}
            >
              {discoverApis.isPending
                ? t('gateway.apis.discover.running')
                : t('gateway.apis.button.discover')}
            </button>
            <button
              type="button"
              onClick={() => { setSelectedApi(null); setDialogMode('create'); }}
              style={primaryBtnStyle}
            >
              {t('gateway.apis.actions.create')}
            </button>
          </div>
        }
      />

      {/* #444 (UAT 2026-05-13): 'API 정보 관리란?' 도움말 패널 — 접힘/펼침. */}
      <div
        style={{
          margin: 'var(--spacing-2) var(--spacing-6) 0',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          aria-expanded={helpOpen}
          aria-label={helpOpen ? t('gateway.apis.help.closeLabel') : t('gateway.apis.help.openLabel')}
          onClick={() => setHelpOpen((v) => !v)}
          style={{
            width: '100%',
            padding: 'var(--spacing-2) var(--spacing-4)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span aria-hidden="true">&#9432;</span>
            {t('gateway.apis.help.title')}
          </span>
          <span aria-hidden="true">{helpOpen ? '▴' : '▾'}</span>
        </button>
        {helpOpen && (
          <div
            style={{
              padding: '0 var(--spacing-4) var(--spacing-3)',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-sm)',
              lineHeight: 1.6,
            }}
          >
            {t('gateway.apis.help.body')}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div
        role="region"
        aria-label={t('gateway.apis.summary.title')}
        style={{
          display: 'flex',
          gap: 'var(--spacing-4)',
          padding: 'var(--spacing-4) var(--spacing-6)',
          flexWrap: 'wrap',
        }}
      >
        <div style={summaryCardStyle}>
          <span style={summaryLabelStyle}>{t('gateway.apis.summary.totalApis')}</span>
          <span style={summaryValueStyle} aria-live="polite">
            {summary.isLoading ? '—' : summary.totalApis}
          </span>
        </div>
        <div style={{ ...summaryCardStyle, borderColor: 'var(--color-success)' }}>
          <span style={summaryLabelStyle}>{t('gateway.apis.summary.activeApis')}</span>
          <span style={{ ...summaryValueStyle, color: 'var(--color-success)' }} aria-live="polite">
            {summary.isLoading ? '—' : summary.activeApis}
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-4) var(--spacing-6)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <select
          aria-label={t('gateway.apis.filter.methodFilter')}
          value={filters.method ?? ''}
          onChange={(e) => setFilters({ ...filters, method: e.target.value || undefined })}
          style={selectStyle}
        >
          <option value="">{t('gateway.apis.filter.allMethods')}</option>
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        <select
          aria-label={t('gateway.apis.filter.statusFilter')}
          value={filters.status ?? ''}
          onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
          style={selectStyle}
        >
          <option value="">{t('gateway.apis.filter.allStatus')}</option>
          <option value="active">{t('gateway.apis.filter.status.active')}</option>
          <option value="deprecated">{t('gateway.apis.filter.status.deprecated')}</option>
          <option value="disabled">{t('gateway.apis.filter.status.disabled')}</option>
        </select>

        <select
          aria-label={t('gateway.apis.filter.authFilter')}
          value={filters.authPolicy ?? ''}
          onChange={(e) => setFilters({ ...filters, authPolicy: e.target.value || undefined })}
          style={selectStyle}
        >
          <option value="">{t('gateway.apis.filter.allAuth')}</option>
          <option value="none">{t('gateway.apis.form.authOptions.none')}</option>
          <option value="jwt">{t('gateway.apis.form.authOptions.jwt')}</option>
          <option value="api-key">{t('gateway.apis.form.authOptions.api-key')}</option>
          <option value="oauth2">{t('gateway.apis.form.authOptions.oauth2')}</option>
        </select>

        <input
          type="search"
          aria-label={t('gateway.apis.filter.searchAriaLabel')}
          placeholder={t('gateway.apis.filter.searchPlaceholder')}
          value={filters.q ?? ''}
          onChange={(e) => setFilters({ ...filters, q: e.target.value || undefined })}
          style={{ ...selectStyle, flex: 1, minWidth: '200px' }}
        />
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 var(--spacing-6) var(--spacing-6)' }}>
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <ComingSoonState />
        ) : !data || data.content.length === 0 ? (
          <EmptyState
            title={t('gateway.apis.list.empty')}
            description={t('gateway.apis.list.emptyDesc')}
            action={
              <button
                type="button"
                onClick={() => { setSelectedApi(null); setDialogMode('create'); }}
                style={primaryBtnStyle}
              >
                {t('gateway.apis.list.emptyAction')}
              </button>
            }
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={data.content}
              rowKey={(row) => row.id}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 'var(--spacing-4)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <span>
                {t('gateway.apis.pagination.range', {
                  total: data.totalElements,
                  start: data.number * data.size + 1,
                  end: Math.min((data.number + 1) * data.size, data.totalElements),
                })}
              </span>
              <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={data.first}
                  onClick={() => setPage((p) => p - 1)}
                  aria-label={t('gateway.apis.pagination.prevAriaLabel')}
                  style={pageBtnStyle}
                >
                  &laquo; {t('gateway.apis.pagination.prev')}
                </button>
                <span style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>
                  {data.number + 1} / {data.totalPages}
                </span>
                <button
                  type="button"
                  disabled={data.last}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label={t('gateway.apis.pagination.nextAriaLabel')}
                  style={pageBtnStyle}
                >
                  {t('gateway.apis.pagination.next')} &raquo;
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Form Dialog */}
      {dialogMode && (
        <ApiFormDialog
          open={!!dialogMode}
          mode={dialogMode}
          initial={selectedApi}
          onClose={() => setDialogMode(null)}
          onSaved={() => setDialogMode(null)}
        />
      )}

      {/* #473: API 자동수집 확인 다이얼로그 */}
      {discoverDialogOpen && (
        <GatewayConfirmDialog
          open={discoverDialogOpen}
          title={t('gateway.apis.discover.confirm.title')}
          description={t('gateway.apis.discover.confirm.description')}
          confirmLabel={t('gateway.apis.button.discover')}
          onConfirm={async () => {
            try {
              await discoverApis.mutateAsync();
            } catch {
              /* onError 콜백이 토스트 노출 */
            }
          }}
          onCancel={() => setDiscoverDialogOpen(false)}
        />
      )}

      {/* Deprecate Confirm */}
      {deprecateTarget && (
        <GatewayConfirmDialog
          open={!!deprecateTarget}
          title={t('gateway.apis.deprecate.confirm')}
          description={t('gateway.apis.deprecate.description')}
          confirmLabel={t('gateway.apis.actions.deprecate')}
          // #450 (UAT 2026-05-14): mutateAsync 로 confirm 완료까지 대기 + 실패 시 토스트.
          onConfirm={async () => {
            try {
              await deprecateApi.mutateAsync(deprecateTarget.id);
            } catch {
              /* onError 콜백이 토스트 노출 */
            }
          }}
          onCancel={() => setDeprecateTarget(null)}
        />
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <GatewayConfirmDialog
          open={!!deleteTarget}
          title={t('gateway.apis.delete.confirm')}
          description={t('gateway.apis.delete.description')}
          destructive
          confirmLabel={t('gateway.shared.actions.delete')}
          // #450 (UAT 2026-05-14): mutate → mutateAsync — confirm 다이얼로그가 요청 완료까지 loading 표시.
          // 성공 시 setDeleteTarget(null) 로 닫힘, 실패 시 다이얼로그 유지 + 에러 토스트.
          onConfirm={async () => {
            try {
              await deleteApi.mutateAsync(deleteTarget.id);
            } catch {
              /* onError 콜백이 토스트 노출 */
            }
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* #450 (UAT 2026-05-14): 삭제/지원종료 결과 토스트 (성공/실패) */}
      {toast && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: toast.kind === 'error' ? 'var(--color-danger)' : 'var(--color-success)',
            color: 'var(--color-text-inverse)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 3000,
            maxWidth: '480px',
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
};

// #473: 자동수집 버튼 — 외곽선 스타일 (primary 색, 채움 없음)
const secondaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-brand-primary)',
  border: '1px solid var(--color-brand-primary)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
};

const actionBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  background: 'var(--color-surface-background)',
  // #442 (UAT 2026-05-13): 다크모드에서 액션 버튼 텍스트는 primary 토큰 사용 (대비 보강).
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-background)',
  // #442 (UAT 2026-05-13): native select 의 시스템 색상 회귀 방지 — 명시적 text token.
  color: 'var(--color-text-primary)',
};

const pageBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface-background)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
};

const summaryCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-1)',
  padding: 'var(--spacing-4) var(--spacing-6)',
  background: 'var(--color-surface-background)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-lg)',
  minWidth: '140px',
  flex: '1 1 140px',
  maxWidth: '240px',
};

const summaryLabelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
};

const summaryValueStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-2xl)',
  fontWeight: 700,
  color: 'var(--color-brand-primary)',
  lineHeight: 1.2,
};
